import { useState, type FC } from "react";
import Tooltip from "../Tooltip";
import Body from "../../atoms/typography/Body";
import { theme } from "../../../theme";
import IconButton from "../../atoms/buttons/IconButton";
import Modal from "../Modal";
import { HorizontalSpacer } from "../../atoms/Spacers";
import ActionButton from "../../atoms/buttons/ActionButton";
import Alert from "../Alert";
import { TrashIcon } from "../../atoms/icons/TrashIcon";
import DefaultButton from "../../atoms/buttons/DefaultButton";
import { useDeleteInvoiceUploadDocument } from "../../../graphql/hooks/documentHandler/useDeleteInvoiceUploadDocument";
import { useDeleteInvoiceUploadUnit } from "../../../graphql/hooks/documentHandler/useDeleteInvoiceUploadUnit";
import { useTranslation } from "react-i18next";

type Props =
  | {
      isDocument: true;
      documentId: string;
      uploadUnitId?: never;
    }
  | {
      isDocument: false;
      uploadUnitId: string;
      documentId?: never;
    };

const DeleteDocumentButton: FC<Props> = ({
  documentId,
  isDocument,
  uploadUnitId,
}) => {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const onCompletedDeleteSuccessful = (result?: any) => {
    if (
      result?.documentHandler?.deleteInvoiceUploadDocument?.success === true ||
      result?.documentHandler?.deleteInvoiceUploadUnit?.success === true
    ) {
      setSuccessMessage(
        isDocument
          ? t("documentHandler.documentHasBeenDeleted")
          : t("documentHandler.uploadHasBeenDeleted"),
      );
    } else {
      setErrorMessage(t("errors.somethingWentWrong"));
    }
  };
  const onErrorDelete = () => {
    setErrorMessage(t("errors.somethingWentWrong"));
  };

  const { deleteInvoiceUploadDocument, error, loading } =
    useDeleteInvoiceUploadDocument({
      onCompleted: onCompletedDeleteSuccessful,
      onError: onErrorDelete,
    });
  const {
    deleteInvoiceUploadUnit,
    error: unitError,
    loading: unitLoading,
  } = useDeleteInvoiceUploadUnit({
    onCompleted: onCompletedDeleteSuccessful,
    onError: onErrorDelete,
  });

  const onConfirmDelete = async () => {
    if (isDocument) {
      try {
        await deleteInvoiceUploadDocument({
          variables: {
            input: {
              uploadDocumentId: documentId,
            },
          },
        });
      } catch (error) {
        setErrorMessage((error as string).toString());
      }
    } else {
      try {
        await deleteInvoiceUploadUnit({
          variables: {
            input: {
              uploadUnitId: uploadUnitId,
            },
          },
        });
      } catch (error) {
        setErrorMessage((error as string).toString());
      }
    }
  };

  const onClickDeleteDocumentButton = (e: React.MouseEvent) => {
    e.stopPropagation();
    setModalOpen(true);
  };

  return (
    <div>
      <Tooltip
        content={
          <Body color={theme.colors.white}>
            {t("common.delete")}{" "}
            {isDocument
              ? t("documentHandler.document").toLowerCase()
              : t("documentHandler.uploadUnit").toLowerCase()}
          </Body>
        }
      >
        <IconButton
          icon={<TrashIcon color={theme.colors.error} />}
          color={theme.colors.error20}
          onClick={onClickDeleteDocumentButton}
          size="medium"
        />
      </Tooltip>

      {modalOpen && (
        <Modal
          onClose={() => setModalOpen(false)}
          closeIcon
          title={`${t("common.delete")} ${isDocument ? t("documentHandler.document").toLowerCase() : t("documentHandler.uploadUnit").toLowerCase()}`}
        >
          <Body textalign="center">
            {isDocument
              ? t("documentHandler.confirmDocumentDelete")
              : t("documentHandler.confirmUploadUnitDelete")}
          </Body>

          {(errorMessage || error || unitError) && (
            <Alert
              type="error"
              title={errorMessage}
              message={
                errorMessage || error?.toString() || unitError?.toString()
              }
            />
          )}

          {successMessage && <Alert title={successMessage} type="success" />}

          <HorizontalSpacer spacing={1} />

          <div style={{ display: "flex", gap: 32, justifyContent: "center" }}>
            <DefaultButton
              title={t("common.cancel")}
              onClick={() => setModalOpen(false)}
            />
            <ActionButton
              title={t("common.delete")}
              loading={loading || unitLoading}
              onClick={onConfirmDelete}
              color={theme.colors.error}
              icon={<TrashIcon color={theme.colors.white} />}
            />
          </div>
        </Modal>
      )}
    </div>
  );
};

export default DeleteDocumentButton;
